import { FunctionComponent } from 'react';
import { Col, FloatingLabel, Row } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { useIMask } from 'react-imask';
import { formatDateTime } from '@xFrame4/common/Functions';
import FieldEditorProps, { FieldEditorLabelPosition, FieldEditorType } from './FieldEditorProps';
import { getDataSourceDisplayMemberValue } from './FieldEditorFunctions';
import CheckboxList, { CheckboxListLayout } from './CheckboxList';
import TripleStateCheckbox from './TripleStateCheckbox';
import AutoComplete from './AutoComplete';
import MultiSelect from './MultiSelect';
import RichTextTextEditor from './RichTextTextEditor';
import NumericStepper from './NumericStepper';
import ColorPicker from './ColorPicker';

const FieldEditor: FunctionComponent<FieldEditorProps> = (props) =>
{
    /** Default props */
    const labelPosition = props.labelPosition ?? FieldEditorLabelPosition.Vertical;
    const isRequired = props.isRequired ?? false;
    const isVisible = props.isVisible ?? true;
    const isEnabled = props.isEnabled ?? true;
    const dataSourceValueMember = props.dataSourceValueMember ?? 'value';
    const dataSourceDisplayMember = props.dataSourceDisplayMember ?? 'display';

    /** Parse the input value in correct format. */
    const parseInput = (value: any) =>
    {
        if (props.type == FieldEditorType.Number && value != null && value != '' && !isNaN(value))
        {
            // Parse as int or float depending on the value
            return Number.isInteger(parseFloat(value)) ? parseInt(value, 10) : parseFloat(value);
        }
        else if (props.type == FieldEditorType.DateTimeLocal && value != null && value != '')
        {
            // If the value is a string, return a datetime string in YYYY-MM-DD hh:mm:ss format
            return formatDateTime(new Date(value));
        }
        else if ((props.type == FieldEditorType.Date || props.type == FieldEditorType.Time || props.type == FieldEditorType.DateTimeLocal) && value == '')
        {
            // Return null if the date or time is empty
            return null;
        }
        else 
        {
            return value;
        }
    };

    /** Phone Mask Hook Setup */
    const isPhoneInput = props.type === FieldEditorType.Phone;
    const maskOptions = {
        mask: props.phoneMask ?? '(000) 000-0000',
        lazy: false,
        unmask: true,
    };
    const onAccept = (unmaskedValue: string) =>
    {
        if (isPhoneInput && props.onValueChanged)
        {
            props.onValueChanged(props.field, unmaskedValue);
        }
    };
    const { ref: phoneMaskRef } = useIMask(isPhoneInput ? maskOptions : {}, { onAccept });

    /** Is the input invalid? */
    let isInvalid = props.invalidFeedbackMessage !== undefined && props.invalidFeedbackMessage != null && props.invalidFeedbackMessage != '';

    /** Create the input control */
    let input: React.ReactNode = <></>;

    if ([
        FieldEditorType.Text,
        FieldEditorType.Email,
        FieldEditorType.Password,
        FieldEditorType.Number,
        FieldEditorType.Date,
        FieldEditorType.Time,
        FieldEditorType.DateTimeLocal,
    ].includes(props.type))
    {
        // Map FieldEditorType.Phone to 'tel'
        const inputType = props.type === FieldEditorType.Phone ? 'tel' : props.type;

        // Parse DateTimeLocal value to the correct format for the HTML input
        let inputValue = props.value ?? '';
        if (props.type === FieldEditorType.DateTimeLocal && props.value)
        {
            // Convert to the format expected by datetime-local input (YYYY-MM-DDTHH:mm)
            try 
            {
                let date = props.value instanceof Date ? props.value : new Date(props.value);
                if (!isNaN(date.getTime()))
                {
                    inputValue = date.toISOString().slice(0, 16);
                }
            }
            catch (e)
            {
                // If parsing fails, use the original value
                inputValue = props.value;
            }
        }

        input =
            <Form.Control
                type={inputType}
                name={props.field}
                value={inputValue} // https://stackoverflow.com/questions/37427508/react-changing-an-uncontrolled-input
                required={isRequired}
                isInvalid={isInvalid}
                disabled={!isEnabled}
                placeholder={props.placeholder}
                aria-label={props.label}
                maxLength={props.maxLength}
                min={props.min}
                max={props.max}
                className={"field-editor-input"}
                onChange={(event) => { if (props.onValueChanged) props.onValueChanged(props.field, parseInput(event.target.value)) }}
                onKeyDown={props.onKeyDown}
                onWheel={(event) => (event.target as HTMLElement).blur()} // https://stackoverflow.com/questions/63224459/disable-scrolling-on-input-type-number-in-react
            />
    }
    else if (props.type == FieldEditorType.Phone)
    {
        input = (
            <Form.Control
                ref={phoneMaskRef as React.RefObject<HTMLInputElement>}
                type="tel"
                name={props.field}
                value={props.value ?? ''}
                required={isRequired}
                isInvalid={isInvalid}
                disabled={!isEnabled}
                placeholder={props.placeholder ?? '(###) ###-####'}
                aria-label={props.label}
                onKeyDown={props.onKeyDown}
                className={"field-editor-input"}
                onWheel={(event) => (event.target as HTMLElement).blur()}
            />
        );
    }
    else if (props.type == FieldEditorType.NumericStepper)
    {
        input =
            <NumericStepper
                field={props.field}
                value={props.value}
                min={typeof props.min === 'number' ? props.min : undefined}
                max={typeof props.max === 'number' ? props.max : undefined}
                step={1}
                isEnabled={isEnabled}
                placeholder={props.placeholder}
                onValueChanged={props.onValueChanged}
            />
    }
    else if (props.type == FieldEditorType.Select)
    {
        let options = props.dataSource?.map(item => 
        {
            let value = item[dataSourceValueMember as string];
            let display = getDataSourceDisplayMemberValue(item, dataSourceDisplayMember as string);

            return <option key={value} value={value}>{display}</option>
        }
        );

        let value;
        if (props.value !== null && props.value !== undefined)
        {
            // Accepts an object or a scalar
            value = typeof props.value == 'object' ? props.value[dataSourceValueMember!] : props.value;
        }

        input =
            <Form.Select
                value={value ?? ''}
                name={props.field}
                required={isRequired}
                isInvalid={isInvalid}
                disabled={!isEnabled}
                aria-label={props.label}
                className={"field-editor-input field-editor-select"}
                onChange={(event) =>
                {
                    if (props.onValueChanged) 
                    {
                        let item = props.dataSource?.find(item =>
                        {
                            return item[dataSourceValueMember!] == event.target.value // Find the selected item based on the selected value
                        });

                        props.onValueChanged(props.field, item); // item can be undefined if not found
                    }
                }}
            >
                {!(props.hideSelectEmpty ?? false) && <option value=""></option>}
                {options}
            </Form.Select>
    }
    else if (props.type == FieldEditorType.List)
    {
        input =
            <CheckboxList
                controlId={props.field}
                dataSource={props.dataSource!}
                dataSourceDisplayMember={dataSourceDisplayMember}
                dataSourceValueMember={dataSourceValueMember}
                disabled={!isEnabled}
                layout={labelPosition == FieldEditorLabelPosition.Floating || labelPosition == FieldEditorLabelPosition.Horizontal ? CheckboxListLayout.Horizontal : CheckboxListLayout.Vertical}
                showSearchBox={props.showSearchBoxForList ?? false}
                showSelectedItems={props.showSelectedItemsForList ?? false}
                className={'field-editor-input'}
                value={props.value ?? []}
                onChange={(selectedItems) => { if (props.onValueChanged) props.onValueChanged(props.field, selectedItems) }}
            />
    }
    else if (props.type == FieldEditorType.AutoComplete)
    {
        input = (
            <AutoComplete
                field={props.field}
                isRequired={isRequired}
                isEnabled={isEnabled}
                isInvalid={isInvalid}
                placeholder={props.placeholder}
                dataSource={props.dataSource}
                dataSourceDisplayMember={dataSourceDisplayMember}
                dataSourceValueMember={dataSourceValueMember}
                value={props.value}
                loadOptions={props.loadAutocompleteOptions}
                onValueChanged={props.onValueChanged}
                className="field-editor-input"
            />
        );
    }
    else if (props.type == FieldEditorType.MultiSelect)
    {
        input = (
            <MultiSelect
                field={props.field}
                isRequired={isRequired}
                isEnabled={isEnabled}
                isInvalid={isInvalid}
                placeholder={props.placeholder}
                dataSource={props.dataSource}
                dataSourceDisplayMember={dataSourceDisplayMember}
                dataSourceValueMember={dataSourceValueMember}
                value={props.value}
                onValueChanged={props.onValueChanged}
                className="field-editor-input"
            />
        );
    }
    else if (props.type == FieldEditorType.Textarea)
    {
        input =
            <Form.Control
                as="textarea"
                name={props.field}
                value={props.value ?? ''}
                rows={props.textareaRows ?? 6}
                required={isRequired}
                isInvalid={isInvalid}
                disabled={!isEnabled}
                placeholder={props.placeholder}
                aria-label={props.label}
                className={"field-editor-input"}
                onChange={(event) => { if (props.onValueChanged) props.onValueChanged(props.field, event.target.value) }}
            />
    }
    else if (props.type == FieldEditorType.RichTextEditor)
    {
        input =
            <RichTextTextEditor
                field={props.field}
                value={props.value}
                rows={props.textareaRows ?? 6}
                isEnabled={isEnabled}
                placeholder={props.placeholder}
                onValueChanged={props.onValueChanged}
                className="field-editor-input"
            />
    }
    else if (props.type == FieldEditorType.Checkbox)
    {
        input =
            <Form.Check
                type="checkbox"
                checked={props.value ? true : false}
                name={props.field}
                label={props.label}
                required={isRequired}
                feedback={props.invalidFeedbackMessage}
                feedbackType="invalid"
                isInvalid={isInvalid}
                disabled={!isEnabled}
                aria-label={props.label}
                className={"field-editor-input " + (isInvalid ? '' : 'd-flex align-items-center')}
                onChange={(event) => { if (props.onValueChanged) props.onValueChanged(props.field, event.target.checked) }}
            />
    }
    else if (props.type == FieldEditorType.TripleStateCheckbox)
    {
        input =
            <TripleStateCheckbox
                value={props.value}
                label={props.label}
                disabled={!isEnabled}
                className={"field-editor-input " + (isInvalid ? '' : 'd-flex align-items-center')}
                onChange={(tripleStateValue) => { if (props.onValueChanged) props.onValueChanged(props.field, tripleStateValue) }}
            />
    }
    else if (props.type == FieldEditorType.File)
    {
        input =
            <Form.Control
                type="file"
                name={props.field}
                multiple={props.allowMultipleFileUpload ?? false}
                ref={props.fileInputRef}
                required={isRequired}
                isInvalid={isInvalid}
                disabled={!isEnabled}
                placeholder={props.placeholder}
                aria-label={props.label}
                className={"field-editor-input"}
                onChange={(event) => fileChangeHandler(event)}
            />
    }
    else if (props.type == FieldEditorType.ColorPicker)
    {
        input =
            <ColorPicker
                field={props.field}
                label={props.label}
                value={props.value}
                defaultValue={undefined}
                isRequired={isRequired}
                isEnabled={isEnabled}
                showHexInput={true}
                placeholder={props.placeholder}
                className={"field-editor-input"}
                invalidFeedbackMessage={props.invalidFeedbackMessage}
                onValueChanged={props.onValueChanged}
            />
    }

    /** Selected file changed. */
    const fileChangeHandler = (event: any) => 
    {
        if (event.target.files != null && event.target.files.length > 0)
        {
            if (props.onValueChanged !== undefined)
            {
                props.onValueChanged(props.field, !props.allowMultipleFileUpload ? event.target.files[0] : event.target.files);
            }
        }
    };

    /** Add label */
    let isCheckbox = props.type == FieldEditorType.Checkbox || props.type == FieldEditorType.TripleStateCheckbox;
    let cmpInputWithLabel = <></>;
    let controlId = 'field-editor-' + props.field;
    let labelPositionClass = '';
    switch (labelPosition)
    {
        case FieldEditorLabelPosition.Vertical:
            {
                labelPositionClass = 'vertical';

                cmpInputWithLabel =
                    <Form.Group
                        controlId={controlId}
                    >
                        {!isCheckbox && <Form.Label>{props.label}</Form.Label>}

                        {props.beforeInput}
                        {input}
                        {props.afterInput}

                        {!isCheckbox &&
                            <div className="invalid-feedback">
                                {props.invalidFeedbackMessage}
                            </div>
                        }
                    </Form.Group>
            }
            break;
        case FieldEditorLabelPosition.Horizontal:
            {
                labelPositionClass = 'horizontal';
                cmpInputWithLabel =
                    <Form.Group
                        as={Row}
                        className="align-items-center"
                        controlId={controlId}
                    >
                        {props.type != FieldEditorType.List &&
                            <Form.Label column sm={4}>
                                {!isCheckbox && <Form.Label>{props.label}</Form.Label>}
                            </Form.Label>
                        }
                        <Col sm={props.type != FieldEditorType.List ? 8 : 12}>
                            {props.beforeInput}
                            {input}
                            {props.afterInput}
                        </Col>
                    </Form.Group>
            }
            break;
        case FieldEditorLabelPosition.Floating:
            {
                labelPositionClass = 'floating';

                cmpInputWithLabel =
                    <FloatingLabel
                        controlId={controlId}
                        label={!isCheckbox && props.label}
                    >
                        {props.beforeInput}
                        {input}
                        {props.afterInput}
                    </FloatingLabel>
            }
            break;
        case FieldEditorLabelPosition.Hidden:
            {
                labelPositionClass = 'hidden';

                cmpInputWithLabel =
                    <Form.Group
                        controlId={controlId}
                    >
                        {props.beforeInput}
                        {input}
                        {props.afterInput}
                    </Form.Group>
            }
            break;
    }

    /** Render */
    return (
        <div
            className={`field-editor field-editor-${props.type} field-editor-label-${labelPositionClass} ${props.className ?? ''}`}
            style={{ display: isVisible ? 'block' : 'none' }}
            data-field-editor-type={props.type}
            data-field-editor-field={props.field}
            data-field-editor-label={props.label}
            data-field-editor-label-position={labelPosition}
            data-field-editor-placeholder={props.placeholder}
            data-field-editor-is-required={isRequired}
            data-field-editor-is-visible={isVisible}
            data-field-editor-is-enabled={isEnabled}
        >
            {cmpInputWithLabel}
        </div>
    );
}

export default FieldEditor;